import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * HoneycombTilemapChart
 * ---------------------
 * Reusable Highcharts tilemap wrapper with honeycomb tiles.
 */
export const HoneycombTilemapChart = ({
  data = [],
  legend,
  title = null,
  subtitle = null,
  dataLabels = true,
  minColor,
  maxColor,
  formatValue,
  onClick,
  colorAxis,
  accessibility,
  tooltipPointFormat,
  tooltipFormatter,
  dataLabelFormatter,
  dataLabelStyle,
  dataLabelOptions,
  seriesName,
  chartHeight,
  inverted = false,
  chartMargins,
  tilemapOptions,
  legendOptions: legendOverrides,
  xAxis: xAxisOverrides,
  yAxis: yAxisOverrides,
}) => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;

  const chartHeightNumber =
    typeof chartHeight === 'number' ? chartHeight
    : typeof chartHeight === 'string' && chartHeight.endsWith('px') ? Number.parseInt(chartHeight, 10)
    : undefined;

  const containerHeight = typeof chartHeight === 'string' ? chartHeight : `${chartHeightNumber ?? 460}px`;
  const containerMinHeight = chartHeightNumber != null ? `${chartHeightNumber}px` : '460px';

  if (!data.length) return null;

  const values = data.map((point) => Number(point.value)).filter((value) => Number.isFinite(value));
  const minVal = values.length ? Math.min(...values) : 0;
  const maxVal = values.length ? Math.max(...values) : 0;

  const legendOptions =
    legend == null ? { enabled: false }
    : legend === 'bottom' ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
    : legend === 'top' ? { enabled: true, align: 'center', verticalAlign: 'top' }
    : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  const mergedLegendOptions = {
    ...legendOptions,
    ...(legendOverrides || {}),
  };

  const chartSpacing =
    legend === 'bottom' ? [8, 8, 24, 8]
    : legend === 'top' ? [24, 8, 8, 8]
    : [8, 8, 8, 8];

  const seriesData = data.map((point, index) => ({
    ...point,
    id: point.id ?? `${point.x}_${point.y}_${index}`,
    x: point.x,
    y: point.y,
    value: point.value,
    name: point.name ?? point.label,
    color: point.color,
    original: point,
  }));

  const valueFormatter =
    formatValue ||
    ((value) => {
      if (value == null || !Number.isFinite(Number(value))) return '-';
      return `${value}`;
    });

  const options = {
    chart: {
      type: 'tilemap',
      inverted,
      ...(chartHeight != null ? { height: chartHeight } : {}),
      ...(chartMargins ?
        {
          marginTop: chartMargins.top,
          marginRight: chartMargins.right,
          marginBottom: chartMargins.bottom,
          marginLeft: chartMargins.left,
        }
      : {}),
      spacing: chartSpacing,
      animation: { duration: 300 },
    },
    title: { text: title },
    subtitle: { text: subtitle },
    accessibility,
    credits: { enabled: false },
    legend: mergedLegendOptions,

    xAxis: {
      visible: false,
      startOnTick: false,
      endOnTick: false,
      gridLineWidth: 0,
      lineWidth: 0,
      tickLength: 0,
      ...xAxisOverrides,
    },
    yAxis: {
      visible: false,
      startOnTick: false,
      endOnTick: false,
      gridLineWidth: 0,
      lineWidth: 0,
      tickLength: 0,
      reversed: true,
      ...yAxisOverrides,
    },

    colorAxis: colorAxis || {
      min: minVal,
      max: maxVal,
      minColor: minColor || theme.palette.error.light,
      maxColor: maxColor || theme.palette.success.main,
    },

    tooltip:
      tooltipFormatter ?
        {
          useHTML: true,
          formatter: tooltipFormatter,
        }
      : tooltipPointFormat ?
        {
          headerFormat: '',
          pointFormat: tooltipPointFormat,
        }
      : {
          useHTML: true,
          formatter: function () {
            const label = this.point.name || `(${this.point.x}, ${this.point.y})`;
            return `<b>${label}</b>: <b>${valueFormatter(this.point.value, this.point.options.original || this.point.options)}</b>`;
          },
        },

    plotOptions: {
      series: {
        borderColor: dividerColor,
        borderWidth: 1,
        animation: { duration: 300 },
        turboThreshold: 0,
      },
      tilemap: {
        tileShape: 'hexagon',
        colsize: 1,
        rowsize: 1,
        pointPadding: 2,
        ...tilemapOptions,
        dataLabels: {
          enabled: dataLabels,
          formatter: function () {
            if (dataLabelFormatter) {
              return dataLabelFormatter(this.point.options.original || this.point.options, this.point);
            }
            return valueFormatter(this.point.value, this.point.options.original || this.point.options);
          },
          style: {
            color: theme.palette.text.primary,
            textOutline: 'none',
            fontSize: '11px',
            fontWeight: 500,
            ...(dataLabelStyle || {}),
          },
          ...(dataLabelOptions || {}),
        },
        cursor: onClick ? 'pointer' : 'default',
        point:
          onClick ?
            {
              events: {
                click: function () {
                  onClick(this.options.original || this.options);
                },
              },
            }
          : {},
      },
    },

    series: [
      {
        id: 'honeycomb-tilemap',
        name: seriesName || title || 'Value',
        type: 'tilemap',
        data: seriesData,
        showInLegend: !!mergedLegendOptions.enabled,
      },
    ],
  };

  return (
    <div style={{ width: '100%', height: containerHeight, minHeight: containerMinHeight }}>
      <BaseHighchart options={options} />
    </div>
  );
};